// src/pages/SecuredData.js — Secured Data (2026-09-15)
//
// Inbound credential delivery. Mint a one-time drop link, hand it to whoever
// holds the secret, and the value lands in encrypted storage without ever
// passing through email, a ticket body, chat, or this screen.
//
// WHY THIS EXISTS: the existing Secure Data Links feature runs the other way
// (us → client) and stores `message` as PLAINTEXT, with file payloads served
// from /uploads, which has no auth and therefore bypasses the OTP, the
// identity lock, max_views, expiry and revoke. Neither is safe for a
// credential, so this is a separate write-only path with its own key.
//
// ⚠️ THERE IS NO REVEAL, AND THERE NEVER WILL BE. The API returns names and
// metadata only — no endpoint returns a stored value. Server-side code reads a
// secret by NAME via getVaultSecret(). If you need a value changed, mint a new
// drop for the same name; it overwrites in place.
import { useState, useEffect, useCallback } from "react";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const panel = {
  background: "var(--t-panel)",
  border: "1px solid var(--t-border-soft)",
  borderRadius: 10,
};
const inputStyle = {
  background: "var(--t-input)",
  border: "1px solid var(--t-border-soft)",
  borderRadius: 8,
  color: "var(--t-text-body)",
  padding: "9px 11px",
  fontSize: 13,
  width: "100%",
};
const btn = (bg) => ({
  background: bg,
  color: "#fff",
  border: 0,
  borderRadius: 8,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
});

const ago = (ts) => {
  if (!ts) return "never";
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SecuredData() {
  const [secrets, setSecrets]   = useState([]);
  const [drops, setDrops]       = useState([]);
  const [enabled, setEnabled]   = useState(true);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [showNew, setShowNew]   = useState(false);
  const [minted, setMinted]     = useState(null);   // {url, secret_name}
  const [copied, setCopied]     = useState(false);
  const [form, setForm]         = useState({ secret_name: "", description: "", expires_hours: "1", kind: "text" });

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        apiFetch("/admin/vault/secrets").then(r => r.json()),
        apiFetch("/admin/vault/drops").then(r => r.json()),
      ]);
      if (s.error) throw new Error(s.error);
      setSecrets(s.secrets || []);
      setDrops(d.drops || []);
      setEnabled(s.vault_enabled !== false);
      setErr("");
    } catch (e) { setErr(e.message || "Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mint = async () => {
    setErr("");
    const r = await apiFetch("/admin/vault/drops", { method: "POST", body: JSON.stringify(form) });
    const j = await r.json();
    if (!r.ok) { setErr(j.error || "Failed to create drop link"); return; }
    setMinted({ url: j.url, secret_name: form.secret_name, kind: form.kind });
    setShowNew(false);
    setForm({ secret_name: "", description: "", expires_hours: "1", kind: "text" });
    load();
  };

  const revoke = async (token) => {
    await apiFetch(`/admin/vault/drops/${token}`, { method: "DELETE" });
    load();
  };
  const delSecret = async (name) => {
    if (!window.confirm(`Delete "${name}"? Anything on the server that reads this secret by name will stop working.`)) return;
    await apiFetch(`/admin/vault/secrets/${encodeURIComponent(name)}`, { method: "DELETE" });
    load();
  };

  const openDrops = drops.filter(d => !d.used_at && !d.revoked && new Date(d.expires_at) > new Date());

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "auto", background: "var(--t-page)" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--t-border-soft)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>
              🔒 Secured Data
            </h1>
            <p style={{ margin: "3px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>
              {secrets.length} stored secret{secrets.length === 1 ? "" : "s"} · {openDrops.length} open drop link{openDrops.length === 1 ? "" : "s"} · values are write-only and can never be read back
            </p>
          </div>
          <button onClick={() => { setShowNew(v => !v); setMinted(null); }} style={btn("#2563eb")}>
            {showNew ? "Cancel" : "New drop link"}
          </button>
        </div>
      </div>

      <div style={{ padding: "18px 24px 40px", display: "flex", flexDirection: "column", gap: 18 }}>
        {!enabled && (
          <div style={{ ...panel, padding: 14, borderColor: "#b45309", background: "rgba(180,83,9,0.12)", color: "#fbbf24", fontSize: 13 }}>
            <b>VAULT_KEY is not configured on the server.</b> The vault is disabled — drop links cannot be created until a 64-character hex key is set.
          </div>
        )}
        {err && (
          <div style={{ ...panel, padding: 12, borderColor: "#b91c1c", background: "rgba(185,28,28,0.12)", color: "#fca5a5", fontSize: 13 }}>{err}</div>
        )}

        {/* Freshly minted link — shown once, deliberately */}
        {minted && (
          <div style={{ ...panel, padding: 16, borderColor: "#16a34a", background: "rgba(22,163,74,0.10)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 6 }}>
              Drop link ready for <code style={{ color: "#7dd3fc" }}>{minted.secret_name}</code>
            </div>
            <div style={{ fontSize: 12, color: "var(--t-text-faint)", marginBottom: 10 }}>
              Send this to whoever holds the {minted.kind === "file" ? "file" : "secret"}. It works once, then burns itself.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input readOnly value={minted.url} onFocus={e => e.target.select()} style={{ ...inputStyle, flex: 1, minWidth: 240, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12 }} />
              <button onClick={() => { navigator.clipboard?.writeText(minted.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }} style={btn("#16a34a")}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}

        {/* New drop form */}
        {showNew && (
          <div style={{ ...panel, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 12 }}>New drop link</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 5 }}>Secret name</label>
                <input value={form.secret_name} onChange={e => setForm(f => ({ ...f, secret_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
                  placeholder="cloudflare_acct1_token" style={{ ...inputStyle, fontFamily: "ui-monospace,Menlo,monospace" }} />
                <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 4 }}>lowercase, digits, underscores · reusing a name overwrites that secret</div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 5 }}>Type</label>
                <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} style={inputStyle}>
                  <option value="text">Text — token, key, password</option>
                  <option value="file">File — cert, .pfx, key file, JSON</option>
                </select>
                <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 4 }}>
                  {form.kind === "file" ? "Malware-scanned, max 10 MB, never written to disk" : "Pasted into a textarea on the drop page"}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 5 }}>Expires in</label>
                <select value={form.expires_hours} onChange={e => setForm(f => ({ ...f, expires_hours: e.target.value }))} style={inputStyle}>
                  <option value="0.25">15 minutes</option>
                  <option value="1">1 hour</option>
                  <option value="4">4 hours</option>
                  <option value="24">24 hours</option>
                  <option value="72">3 days</option>
                </select>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 5 }}>What is it? (optional)</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Cloudflare API token for the 23 Champion zones" style={inputStyle} />
              </div>
            </div>
            <button onClick={mint} disabled={!form.secret_name.trim()} style={{ ...btn("#2563eb"), marginTop: 14, opacity: form.secret_name.trim() ? 1 : 0.5 }}>
              Create drop link
            </button>
          </div>
        )}

        {/* Stored secrets */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>Stored secrets</div>
          {loading ? (
            <div style={{ color: "var(--t-text-faint)", fontSize: 13 }}>Loading…</div>
          ) : secrets.length === 0 ? (
            <div style={{ ...panel, padding: 16, color: "var(--t-text-faint)", fontSize: 13 }}>
              Nothing stored yet. Create a drop link above and send it to whoever holds the credential.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {secrets.map(s => (
                <div key={s.name} style={{ ...panel, padding: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 13, fontWeight: 700, color: "#7dd3fc" }}>{s.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: s.kind === "file" ? "rgba(168,85,247,0.18)" : "rgba(59,130,246,0.18)", color: s.kind === "file" ? "#c4b5fd" : "#93c5fd" }}>
                        {s.kind === "file" ? "FILE" : "TEXT"}
                      </span>
                    </div>
                    {s.description && <div style={{ fontSize: 12, color: "var(--t-text-body)", marginTop: 2 }}>{s.description}</div>}
                    {s.kind === "file" && (
                      <div style={{ fontSize: 11, color: "var(--t-text-body)", marginTop: 3, fontFamily: "ui-monospace,Menlo,monospace" }}>
                        {s.filename} · {s.byte_size != null ? `${(s.byte_size / 1024).toFixed(1)} KB` : "?"} · sha256 {String(s.sha256 || "").slice(0, 12)}…
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 3 }}>
                      updated {ago(s.updated_at)} · read {s.access_count}× · last read {ago(s.last_accessed_at)}
                      {s.created_by_name ? ` · by ${s.created_by_name}` : ""}
                    </div>
                  </div>
                  <button onClick={() => delSecret(s.name)} style={{ ...btn("transparent"), color: "#f87171", border: "1px solid #7f1d1d", padding: "6px 12px" }}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Drop links */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)", marginBottom: 8 }}>Drop links</div>
          {drops.length === 0 ? (
            <div style={{ ...panel, padding: 16, color: "var(--t-text-faint)", fontSize: 13 }}>No drop links yet.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {drops.map(d => {
                const expired = new Date(d.expires_at) < new Date();
                const state = d.used_at ? { t: "used", c: "#64748b" }
                  : d.revoked ? { t: "revoked", c: "#f87171" }
                  : expired ? { t: "expired", c: "#64748b" }
                  : { t: "open", c: "#34d399" };
                return (
                  <div key={d.token} style={{ ...panel, padding: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", opacity: state.t === "open" ? 1 : 0.6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: state.c, flexShrink: 0 }} />
                        <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>{d.secret_name}</span>
                        <span style={{ fontSize: 11, color: state.c, fontWeight: 700 }}>{state.t}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--t-text-faint)" }}>{d.kind === "file" ? "FILE" : "TEXT"}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 3 }}>
                        created {ago(d.created_at)}
                        {d.used_at ? ` · used ${ago(d.used_at)}` : ` · expires ${new Date(d.expires_at).toLocaleString()}`}
                        {d.created_by_name ? ` · by ${d.created_by_name}` : ""}
                      </div>
                    </div>
                    {state.t === "open" && (
                      <button onClick={() => revoke(d.token)} style={{ ...btn("transparent"), color: "#f87171", border: "1px solid #7f1d1d", padding: "6px 12px" }}>Revoke</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
